/**
 * #769 — "is it unsafe to silently reload right now?"
 *
 * Conservative by design: a wrongly-timed auto-reload that drops a user's
 * half-typed QC entry or cuts off a streaming answer is far worse than showing
 * a toast they can tap. So this errs toward "busy" — when in doubt, we toast
 * instead of reload. The two things that must never be interrupted:
 *
 *   1. An in-flight chat / stream (tracked explicitly via busy-signal.ts).
 *   2. Unsaved form input — the user actively typing, or a real data-entry
 *      form (e.g. QA Entry) holding entered-but-unsubmitted text.
 *
 * Heuristic notes:
 * - "Actively typing" = the focused element is a text-entry control. Even if
 *   empty we hold off, because the user's attention is in a field.
 * - "Unsubmitted form data" = a non-empty text input/textarea *inside a
 *   <form>*. Scoping to <form> avoids a standalone dashboard search box (not in
 *   a form) blocking auto-reload forever, while still catching genuine entry
 *   forms. AC #1 (idle tab refreshes on refocus) needs standalone filters to
 *   NOT count as busy.
 */

import { isChatBusy } from "./busy-signal";
import { queryClient } from "./query-client";

const TEXT_INPUT_TYPES = new Set([
  "text",
  "search",
  "email",
  "url",
  "tel",
  "password",
  "number",
  "",
]);

function isTextEntryElement(el: Element | null): el is HTMLElement {
  if (!el) return false;
  const tag = el.tagName;
  if (tag === "TEXTAREA") return true;
  if (tag === "INPUT") {
    const type = (el as HTMLInputElement).type?.toLowerCase() ?? "";
    return TEXT_INPUT_TYPES.has(type);
  }
  return (el as HTMLElement).isContentEditable === true;
}

function hasEnteredValue(el: Element): boolean {
  if (el.tagName === "INPUT" || el.tagName === "TEXTAREA") {
    return ((el as HTMLInputElement | HTMLTextAreaElement).value ?? "").trim().length > 0;
  }
  if ((el as HTMLElement).isContentEditable) {
    return ((el as HTMLElement).textContent ?? "").trim().length > 0;
  }
  return false;
}

/**
 * Returns true when it would be unsafe to auto-reload. `doc` is injectable for
 * tests, but the DOM scan is a heuristic and not the unit-tested core — the
 * reload-vs-toast decision itself lives in the pure `decideVersionAction`.
 */
export function isAppBusy(doc: Document | undefined = typeof document !== "undefined" ? document : undefined): boolean {
  // In-flight chat/stream — never interrupt.
  if (isChatBusy()) return true;

  // #1265: an in-flight mutation (a save, a file upload) was invisible to the
  // DOM heuristics — the user may have already submitted and cleared the form
  // when the reload cuts the request off. The shared QueryClient knows.
  if (queryClient.isMutating() > 0) return true;

  if (!doc) return false;

  // #1265: an open modal/dialog is a task in progress even before any text is
  // typed (a confirm step, a file picker, a wizard). Radix/shadcn dialogs all
  // render role="dialog".
  if (doc.querySelector('[role="dialog"]')) return true;

  // Actively typing somewhere.
  if (isTextEntryElement(doc.activeElement)) return true;

  // A data-entry form holding unsubmitted text.
  const formFields = doc.querySelectorAll("form input, form textarea, form [contenteditable='true']");
  for (const el of formFields) {
    if (isTextEntryElement(el) && hasEnteredValue(el)) return true;
  }

  return false;
}
